import type { CSSProperties } from "react";
import type { Event } from "../../types/Types";
import { isEventEarlyBirdConfigured } from "../../utils/eventEarlyBirdUtils";
import type { EventCardPhase } from "../../utils/eventDateUtils";

export const PAGE_SIZE = 20;
export const EVENT_QUESTIONS_PATH = "/event-questions";
export const EVENT_ATTENDEES_PATH = "/event-booking/attendees";
export const SAVED_EVENTS_STORAGE_KEY = "smbf-saved-events";
export const ATTENDEE_AVATAR_PREVIEW_COUNT = 3;
export const ATTENDEE_AVATAR_SIZE_PX = 18;

export const EVENT_CARD_IMAGES = Array.from(
  { length: 10 },
  (_, i) => `/SMBF-${26 + i}.jpg` as const,
);

const CARD_BASE = "card rounded-3 overflow-hidden shadow-sm w-100";
export const PHASE_CARD_CLASS: Record<EventCardPhase, string> = {
  upcoming: `${CARD_BASE} border-0 bg-white`,
  today: `${CARD_BASE} border border-white border-2 bg-white`,
  expired: `${CARD_BASE} border-0 bg-light`,
};
export const PHASE_BADGE_CLASS: Record<EventCardPhase, string> = {
  today: "badge bg-primary text-white text-uppercase small",
  upcoming: "badge bg-success text-white text-uppercase small",
  expired: "badge bg-white text-danger border border-danger text-uppercase small",
};

export const META_CLASS = "text-muted lh-sm mb-0";
export const META_STYLE: CSSProperties = { fontSize: "13px" };
export const SLOT = {
  title: { minHeight: "1.35rem" } as CSSProperties,
  desc: { minHeight: "2.25rem" } as CSSProperties,
  location: { ...META_STYLE, minHeight: "1.125rem" } as CSSProperties,
  pricing: { minHeight: "3.25rem" } as CSSProperties,
};

export const FILTER_STYLE: CSSProperties = { minWidth: 140, flexBasis: "12%" };
export const EARLY_BIRD_FILTER_STYLE: CSSProperties = {
  minWidth: 160,
  flexBasis: "14%",
};

export const EN_MONTH_LONG = Array.from({ length: 12 }, (_, m) =>
  new Date(2000, m, 1).toLocaleString("en", { month: "long" }).toLowerCase(),
);
export const EN_MONTH_SHORT = Array.from({ length: 12 }, (_, m) =>
  new Date(2000, m, 1).toLocaleString("en", { month: "short" }).toLowerCase(),
);

export type EventListFilters = {
  city: string;
  country: string;
  price: "" | "free" | "paid";
  earlyBirdOnly: boolean;
};
export type EventsListMode = "upcoming" | "attended" | "saved";

export const EMPTY_FILTERS: EventListFilters = {
  city: "",
  country: "",
  price: "",
  earlyBirdOnly: false,
};

export const TABS: { id: EventsListMode; icon: string; labelKey: string }[] = [
  { id: "upcoming", icon: "bi-calendar3", labelKey: "events.tabUpcoming" },
  { id: "attended", icon: "bi-check-circle", labelKey: "events.tabAttendedEvents" },
  { id: "saved", icon: "bi-bookmark", labelKey: "events.tabSavedEvents" },
];

export function readSavedEventsFromStorage(): Event[] {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(SAVED_EVENTS_STORAGE_KEY) || "null",
    );
    return Array.isArray(parsed) ? (parsed as Event[]) : [];
  } catch {
    return [];
  }
}

export function writeSavedEventsToStorage(events: Event[]) {
  try {
    localStorage.setItem(SAVED_EVENTS_STORAGE_KEY, JSON.stringify(events));
  } catch {
    /* ignore */
  }
}

export function eventMatchesListFilters(
  event: Event,
  filters: EventListFilters,
): boolean {
  const location = (event.location ?? "").toLowerCase();
  const city = filters.city.trim().toLowerCase();
  const country = filters.country.trim().toLowerCase();
  if (city && !location.includes(city)) return false;
  if (country && !location.includes(country)) return false;
  if (filters.price === "free" && (event.price ?? 0) > 0) return false;
  if (filters.price === "paid" && (event.price ?? 0) <= 0) return false;
  if (filters.earlyBirdOnly && !isEventEarlyBirdConfigured(event)) return false;
  return true;
}

export function sortByDate(list: Event[], desc = false) {
  return list.sort((a, b) => {
    const diff =
      new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime();
    return desc ? -diff : diff;
  });
}

export function filterOptionClass() {
  return "btn btn-sm rounded-3 border bg-white text-dark d-flex align-items-center justify-content-between gap-2 px-3 py-2 shadow-sm w-100";
}

export const FILTER_PILL_ACTIVE_STYLE: CSSProperties = {
  borderColor: "#2366CD",
  color: "#2366CD",
  backgroundColor: "rgba(35, 102, 205, 0.06)",
};

export const FILTER_ITEM_ACTIVE_STYLE: CSSProperties = {
  background: "linear-gradient(90deg, #2366CD 0%, #3A97A4 100%)",
  color: "#fff",
};

export const FILTER_ITEM_HOVER_STYLE: CSSProperties = {
  backgroundColor: "rgba(35, 102, 205, 0.1)",
  color: "#2366CD",
};
